// ============================================================================
// I18nProvider — React-провайдер языка интерфейса (этап 2 мультиязычности).
//
// Монтируется ОДИН раз над приложением (main.tsx). Источники языка и загрузка
// словарей — lib/i18n/store.ts (SSoT): настройка пользователя → язык базы.
// Провайдер только связывает стор с React и перерисовывает подписчиков при
// смене языка/загрузке серверных переводов.
// ============================================================================

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { useAuthStore } from '@/store/authStore';
import { useCenterStore } from '@/store/centerStore';
import { settingsStore } from '@/lib/settingsStore';
import {
  getI18nSnapshot, loadI18nResources, setLocale as setLocaleInStore,
  subscribeI18n, syncLocaleFromSettings, translate,
  type I18nLanguage, type I18nSnapshot,
} from './store';

export interface I18nContextValue extends I18nSnapshot {
  /** Перевод ключа (явный fallback: текущая локаль → язык базы → ключ). */
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** Сменить язык интерфейса (настройка пользователя `ui.locale`). */
  setLocale: (code: string) => void;
  /** Доступные языки (встроенные + языки базы). */
  languages: I18nLanguage[];
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [snap, setSnap] = useState<I18nSnapshot>(getI18nSnapshot);
  const baseSlug = useCenterStore((s) => s.baseSlug);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => subscribeI18n(() => setSnap(getI18nSnapshot())), []);

  // Смена базы/вход: пользовательские настройки живут В СХЕМЕ БАЗЫ — сначала
  // гидрация, затем применяем сохранённый язык и грузим словари.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (isAuthenticated) {
        try {
          await settingsStore.hydrate();
        } catch {
          // hydrate сам логирует; язык продолжим на языке базы/встроенном.
        }
      }
      if (cancelled) return;
      syncLocaleFromSettings();
      await loadI18nResources();
    })();
    return () => { cancelled = true; };
  }, [baseSlug, isAuthenticated]);

  // Живая реакция на запись настройки (в т.ч. гидрация/другое окно).
  useEffect(() => settingsStore.subscribe(() => syncLocaleFromSettings()), []);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => translate(key, vars),
    // translate читает актуальный снапшот из стора; revision — сигнал изменения.
    [snap.revision],
  );
  const setLocale = useCallback((code: string) => setLocaleInStore(code), []);

  const value = useMemo<I18nContextValue>(() => ({
    ...snap,
    languages: snap.languages,
    t,
    setLocale,
  }), [snap, t, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Доступ к языку интерфейса. Использовать ТОЛЬКО внутри I18nProvider. */
export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return ctx;
}

/** Сокращение для компонентов, которым нужен только перевод. */
export function useT(): I18nContextValue['t'] {
  return useI18n().t;
}
